/**
 * PerfCollector - 效能收集器
 *
 * 收集編輯器效能樣本並計算統計數據
 * Requirements: 7.2, 7.3, 7.6
 */

import type { PerfSampleKind, EditorPerfSample, PerfStats } from '../types';

/** 效能閾值配置 */
interface ThresholdConfig {
  warn: number;
  error: number;
}

/** 各類型的效能閾值 (ms) */
const THRESHOLDS: Record<PerfSampleKind, ThresholdConfig> = {
  keypress: { warn: 20, error: 50 },
  render: { warn: 16, error: 50 },
  parse: { warn: 30, error: 50 },
  serialize: { warn: 30, error: 50 },
  modeSwitch: { warn: 50, error: 100 },
};

/**
 * 計算百分位數
 * @param sortedValues 已排序的數值陣列
 * @param percentile 百分位數 (0-100)
 */
function calculatePercentile(sortedValues: number[], percentile: number): number {
  if (sortedValues.length === 0) return 0;
  const index = Math.ceil((percentile / 100) * sortedValues.length) - 1;
  return sortedValues[Math.max(0, index)] ?? 0;
}

/**
 * PerfCollector 類別
 *
 * 收集效能樣本並提供統計分析功能
 */
export class PerfCollector {
  private samples: EditorPerfSample[] = [];
  private readonly maxSamples: number;

  constructor(maxSamples = 1000) {
    this.maxSamples = maxSamples;
  }

  /**
   * 收集效能樣本
   * @param kind 樣本類型
   * @param durationMs 持續時間 (ms)
   * @param metadata 額外元資料
   */
  sample(kind: PerfSampleKind, durationMs: number, metadata?: Record<string, unknown>): void {
    const sample: EditorPerfSample = {
      kind,
      durationMs,
      ts: Date.now(),
      metadata,
    };

    this.samples.push(sample);

    // 超過最大樣本數時移除最舊的
    if (this.samples.length > this.maxSamples) {
      this.samples.shift();
    }

    // 檢查是否超標
    this.checkThreshold(kind, durationMs);
  }

  /**
   * 取得指定類型的效能統計
   * @param kind 樣本類型
   */
  getStats(kind: PerfSampleKind): PerfStats {
    const kindSamples = this.samples.filter((s) => s.kind === kind).map((s) => s.durationMs);

    if (kindSamples.length === 0) {
      return { p50: 0, p95: 0, p99: 0, count: 0, max: 0 };
    }

    // 排序以計算百分位數
    const sorted = [...kindSamples].sort((a, b) => a - b);

    return {
      p50: calculatePercentile(sorted, 50),
      p95: calculatePercentile(sorted, 95),
      p99: calculatePercentile(sorted, 99),
      count: sorted.length,
      max: sorted[sorted.length - 1] ?? 0,
    };
  }

  /**
   * 取得最近的樣本
   * @param count 樣本數量 (預設: 100)
   */
  getRecentSamples(count = 100): EditorPerfSample[] {
    return this.samples.slice(-count);
  }

  /**
   * 清除所有樣本
   */
  clear(): void {
    this.samples = [];
  }

  /**
   * 檢查效能是否超標並發出警告
   * @param kind 樣本類型
   * @param durationMs 持續時間 (ms)
   */
  private checkThreshold(kind: PerfSampleKind, durationMs: number): void {
    const threshold = THRESHOLDS[kind];

    if (durationMs >= threshold.error) {
      console.warn(
        `[PerfCollector] ${kind} latency exceeded error threshold: ${durationMs}ms (threshold: ${threshold.error}ms)`
      );
    } else if (durationMs >= threshold.warn) {
      console.warn(
        `[PerfCollector] ${kind} latency exceeded warn threshold: ${durationMs}ms (threshold: ${threshold.warn}ms)`
      );
    }
  }
}

/** 全域 PerfCollector 實例 */
export const perfCollector = new PerfCollector();
